import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { ReactionDisplay } from "@/modules/social/components/reactionDisplay"
import { SocialCommentInput } from "@/modules/social/components/socialCommentInput"
import { SocialCommentList } from "@/modules/social/components/socialCommentList"
import { useSessionSocialPost } from "@/modules/social/hooks/useSocialPost"
import { createSocialPostModel } from "@/modules/social/models/socialPostModel"
import { LoadingPage } from "@/modules/ui/components/loadingPage"
import { Suspense, useRef, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

export type SocialViewProps = {
  socialPostId: string | null
  sessionInfos: { id: string; name: string }
}

export function SocialView({ socialPostId, sessionInfos }: SocialViewProps) {
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const [parentCommentId, setParentCommentId] = useState<string | null>(null)
  const [replyTo, setReplyTo] = useState<string>()
  const [autoExpandCommentId, setAutoExpandCommentId] = useState<string | null>(null)

  const onCommentSubmit = () => {
    setAutoExpandCommentId(parentCommentId)
    setParentCommentId(null)
    setReplyTo(undefined)
  }

  const onCommentReply = (commentId: string, username?: string) => {
    setParentCommentId(commentId)
    setReplyTo(username)
    commentInputRef.current?.focus()
  }

  const onCancelReply = () => {
    setParentCommentId(null)
    setReplyTo(undefined)
  }

  return (
    <div className="bg-surface-background-primary relative flex h-full w-full flex-col">
      <div className="flex-1 overflow-y-auto p-4 pb-32">
        <div className="mt-2 mb-6 flex items-center justify-center">
          <Text fontStyle="HS" className="text-textOnSurface-default">
            {sessionInfos.name}
          </Text>
        </div>

        <ErrorBoundary
          fallback={
            <div className="flex items-center justify-center py-12">
              <Text fontStyle="BM" className="text-feedback-error-text">
                {word("social.view.error")}
              </Text>
            </div>
          }
        >
          <Suspense fallback={<LoadingPage />}>
            {socialPostId && <SocialViewPostReactions socialPostId={socialPostId} />}

            <div className="flex flex-col gap-4">
              <Text fontStyle="TL" className="text-textOnSurface-default">
                {word("social.view.comments.title")}
              </Text>

              <SocialCommentList
                socialPostId={socialPostId}
                onReply={onCommentReply}
                autoExpandCommentId={autoExpandCommentId}
              />
            </div>
          </Suspense>
        </ErrorBoundary>
      </div>

      <div className="sticky right-0 bottom-0 left-0 bg-surface-background-default">
        <SocialCommentInput
          socialPostId={socialPostId}
          parentCommentId={parentCommentId}
          replyTo={replyTo}
          ref={commentInputRef}
          onSubmit={onCommentSubmit}
          onCancelReply={onCancelReply}
          sessionId={sessionInfos.id}
        />
      </div>
    </div>
  )
}

function SocialViewPostReactions({ socialPostId }: { socialPostId: string }) {
  const { data: post } = useSessionSocialPost(socialPostId)
  const model = createSocialPostModel(post)

  if (model.isEmpty) return null

  return (
    <div className="py-2">
      <ReactionDisplay
        displayedReactions={model.displayedReactions}
        totalReactions={model.totalReactions}
        commentsNumber={model.totalComments}
      />
    </div>
  )
}
